import { useSyncExternalStore, useCallback } from 'react';

// 主题类型
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'pingo-theme';

// --- 模块级状态管理 ---

// 订阅者集合
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

// useSyncExternalStore 的 subscribe 函数
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// useSyncExternalStore 的 getSnapshot 函数（单一真相源：body attribute）
function getSnapshot(): Theme {
  return document.body.getAttribute('arco-theme') === 'dark' ? 'dark' : 'light';
}

// 核心主题切换逻辑
function applyTheme(theme: Theme) {
  // 添加过渡类（如果 CSS 已加载，则触发平滑过渡）
  document.body.classList.add('theme-transitioning');

  // 应用主题
  if (theme === 'dark') {
    document.body.setAttribute('arco-theme', 'dark');
  } else {
    document.body.removeAttribute('arco-theme');
  }

  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // 忽略 localStorage 不可用（隐私模式）
  }

  emitChange();

  // 过渡完成后移除过渡类（350ms > transition 的 300ms）
  requestAnimationFrame(() => {
    setTimeout(() => {
      document.body.classList.remove('theme-transitioning');
    }, 350);
  });
}

// 模块级系统偏好监听（仅当用户未手动设置时跟随系统）
try {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', (e) => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    } catch {
      // localStorage 不可用时，始终跟随系统
      applyTheme(e.matches ? 'dark' : 'light');
    }
  });
} catch {
  // matchMedia 不可用（极端环境），静默忽略
}

// --- Hook ---

/**
 * 主题切换 Hook（基于 useSyncExternalStore，多消费者安全）
 *
 * body[arco-theme='dark'] 属性作为单一真相源，
 * 多个组件同时调用 useTheme() 时状态自动同步。
 */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot);

  const setTheme = useCallback((t: Theme) => {
    applyTheme(t);
  }, []);

  const toggleTheme = useCallback(() => {
    const current = getSnapshot();
    applyTheme(current === 'dark' ? 'light' : 'dark');
  }, []);

  return { theme, setTheme, toggleTheme } as const;
}
